import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { WebRTCAdaptor } from "@antmedia/webrtc_adaptor";
import { Box, Card, Grid, IconButton, TextField, Typography, useTheme } from "@material-ui/core";
import { Mic, MicOff, Videocam, VideocamOff } from "@material-ui/icons";

import FileCopy from "@material-ui/icons/FileCopy";
import { Alert } from "@material-ui/lab";

import Button from "@remar/shared/dist/components/Button";
import ChatComponent, { DataTypes, WebRTCAdaptorStatus } from "@remar/shared/dist/components/ChatComponent";
import { Wrapper } from "@remar/shared/dist/layouts";
import { LessonComment } from "@remar/shared/dist/models";
import { IExtendedTheme } from "@remar/shared/dist/theme/default";
import { getVideoPlayer } from "@remar/shared/dist/utils/serviceUtils/helpers";
import { differenceInSeconds } from "date-fns";
import { useHistory, useParams } from "react-router-dom";
import { selectUser } from "store/features/Auth/auth.slice";

import {
	AddNewComment,
	clearSelectedStream,
	deleteLiveStreamComment,
	fetchLiveStream,
	fetchLiveStreamComments,
	fetchLiveStreamViewersCount,
	getLiveStreamFullState,
	postLiveStreamComment,
	updateLiveStream
} from "store/features/LiveStream/LiveStream.slice";
import { emit } from "store/features/notifications/notifications.slice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import videojs from "video.js";

import "video.js/dist/video-js.min.css";

import { GLOBAL_CONSTANTS, routes } from "core/constants";

import { StyledEye, VideoContainer } from "./styles";

const StreamVideo = () => {
	const dispatch = useAppDispatch();
	const history = useHistory();
	const theme = useTheme<IExtendedTheme>();
	const player = useRef<videojs.Player>(null);
	const adaptorRef = useRef<WebRTCAdaptor>(null);
	const { id, streamId } = useParams<{ id: string; streamId: string }>();

	const [isLoading, setIsLoading] = useState(true);
	const [isOnline, setIsOnline] = useState(false);
	const [isInitialized, setIsInitialized] = useState(false);
	const [isMute, setIsMute] = useState(false);
	const [isCameraEnabled, setIsCameraEnabled] = useState(true);

	const { selectedLiveStream, isFetchingStream, selectedLiveStreamViewersCount, streamComments, isFetchingComments } =
		useAppSelector(getLiveStreamFullState);
	const user = useAppSelector(selectUser);

	const breadcrumb = useMemo(
		() => [
			{ title: "Live Stream", key: 0, link: routes.stream.getPath() },
			{ title: "Create Live Stream", key: 1, link: routes.stream.createNew.getPath() },
			{ title: selectedLiveStream?.name, key: 2 }
		],
		[selectedLiveStream]
	);

	useEffect(() => {
		if (!id) return;
		dispatch(
			fetchLiveStream({
				id
			})
		);
	}, [dispatch, id]);

	useEffect(() => {
		if (!isOnline) return;
		const startPolling = async () => {
			dispatch(fetchLiveStreamViewersCount({ streamId }));
		};

		startPolling();

		const interval = setInterval(() => {
			startPolling();
		}, 5000);

		return () => clearInterval(interval);
	}, [streamId, isOnline, dispatch]);

	useEffect(() => {
		dispatch(fetchLiveStreamComments({ streamId: id }));
	}, [dispatch, id]);

	useEffect(() => {
		if (isInitialized) {
			if (selectedLiveStream && !selectedLiveStream.hasEnded) {
				publishStream(streamId, selectedLiveStream.publishToken!);
			}
		}
	}, [isInitialized, selectedLiveStream, streamId]);

	useEffect(() => {
		if (
			!streamId ||
			!selectedLiveStream ||
			(selectedLiveStream.hasEnded && selectedLiveStream.durationInSeconds > 0) ||
			!!adaptorRef.current
		)
			return;

		adaptorRef.current = new WebRTCAdaptor({
			websocket_url: `${GLOBAL_CONSTANTS.ANT_MEDIA_SERVER_URL}/websocket`,
			mediaConstraints: {
				video: true,
				audio: true
			},
			peerconnection_config: {
				iceServers: JSON.parse(GLOBAL_CONSTANTS.ICE_SERVERS as string)
			},
			sdp_constraints: {
				OfferToReceiveAudio: false,
				OfferToReceiveVideo: false
			},
			publishToken: selectedLiveStream.publishToken,
			localVideoId: "video-player-stream",
			bandwidth: 900, // default is 900 kbps, string can be 'unlimited'
			dataChannelEnabled: true, // enable or disable data channel
			debug: true,
			callback: (info, obj) => {
				console.log("ℹ️ WebRTCAdaptor Callback: ", info, obj);
				switch (info) {
					case WebRTCAdaptorStatus.INITIALIZED: {
						console.log("✅ WebRTCAdaptor initialized.");
						setIsInitialized(true);
						break;
					}
					case WebRTCAdaptorStatus.STATE_CHANGED: {
						if (obj.state === "checking") {
							setIsLoading(true);
						}
						if (obj.state === "connected") {
							setIsLoading(false);
							setIsOnline(true);
						}
						break;
					}
					case WebRTCAdaptorStatus.PUBLISH_STARTED: {
						console.log("🎦 Publishing started");
						dispatch(emit({ message: "Stream has started!", color: "success" }));
						break;
					}
					case WebRTCAdaptorStatus.PUBLISH_FINISHED: {
						console.log("🏁 Publishing stopped");
						break;
					}
					case WebRTCAdaptorStatus.CLOSED: {
						console.log(`🛑 Connection closed for streamId: ${streamId}`);
						break;
					}
					case WebRTCAdaptorStatus.RECONNECTING: {
						console.log("🔃 trying to reconnect...");
						break;
					}
					case WebRTCAdaptorStatus.UPDATED_STATS: {
						console.log("📊 Update stats: ", obj);
						console.log(`📈 Stream ID: ${obj.streamId}, Viewers: ${obj.webRTCViewerCount}`);
						break;
					}
					case WebRTCAdaptorStatus.NUMBER_VIEWERS: {
						console.log(`📈 Stream ID: ${obj.streamId}, Viewers: ${obj.viewerCount}`);
						break;
					}
					case WebRTCAdaptorStatus.PLAY_STARTED: {
						console.log("▶️ Playing started");
						break;
					}
					case WebRTCAdaptorStatus.PLAY_FINISHED: {
						console.log("⛔️Playing stopped");
						break;
					}
					case WebRTCAdaptorStatus.STREAM_INFO: {
						console.log("📡 Stream info: ", obj);
						break;
					}
					case WebRTCAdaptorStatus.DATA_CHANNEL_OPENED: {
						console.info("💬 Data channel opened");
						break;
					}
					case WebRTCAdaptorStatus.DATA_RECEIVED: {
						const data = JSON.parse(obj.data);
						if (data && data.dataType === DataTypes.Chat) {
							const { id, message: text, sendBy: fullName, timeStamp: createdAt, isDeleted, user } = data;
							handleChatMessageReceive({
								id: Number(id),
								text,
								user: { ...user, fullName },
								createdAt,
								isDeleted
							});
						}
						break;
					}
					case WebRTCAdaptorStatus.DATA_CHANNEL_ERROR: {
						console.error("❌ Data channel error", obj);
						break;
					}
					case WebRTCAdaptorStatus.DATA_CHANNEL_CLOSED: {
						console.info("❎ Data channel closed");
						break;
					}
				}
			},
			callbackError: (error, message) => {
				console.error(`🔥 WebRtcError: ${message}}`, error);
			}
		});

		return () => {
			if (adaptorRef.current) {
				adaptorRef.current?.closeWebSocket();
				adaptorRef.current?.closeStream();
				adaptorRef.current = null;
			}
		};
	}, [dispatch, streamId, selectedLiveStream]);

	useEffect(() => {
		if (player.current) {
			isLoading ? player.current.loadingSpinner.show() : player.current.loadingSpinner.hide();
		}
	}, [isLoading]);

	const handleUpdateStream = (streamId: string, startTime: string): void => {
		const timeNow = new Date().getTime();
		const durationInSeconds = differenceInSeconds(timeNow, +startTime);

		dispatch(
			updateLiveStream({
				id,
				data: { durationInSeconds, hasEnded: true },
				cb: () => localStorage.removeItem(`stream-start-${streamId}`)
			})
		);
	};

	const configurePlayer = useCallback(() => {
		const videoPlayer = selectedLiveStream!.hasEnded
			? getVideoPlayer("video-player-stream", selectedLiveStream!.streamLink!)
			: videojs("video-player-stream", {
					controls: true,
					autoplay: true,
					muted: true,
					controlBar: {
						playToggle: false,
						volumePanel: false,
						fullscreenToggle: true,
						currentTimeDisplay: false,
						timeDivider: false,
						durationDisplay: false,
						remainingTimeDisplay: false,
						progressControl: false
					},
					userActions: {
						doubleClick: false,
						hotkeys: false,
						click: false
					},
					liveui: true
			  });

		console.log("⚙️ Configure new player", streamId);
		videoPlayer.on("error", (_, errors) => console.error("🔥 Video.js encountered an error", errors));
		videoPlayer.on("ant-error", (_, errors) => console.error("🔥 Error during WebRTC playback:", errors));
		videoPlayer.on("play", () => {
			console.log("Player played 🎵");

			if (!selectedLiveStream.hasEnded) {
				localStorage.setItem(`stream-start-${streamId}`, "" + new Date().getTime());
			}
		});
		videoPlayer.on("playing", () => console.log("🎶 Video is Playing"));
		videoPlayer.on("canplay", () => videoPlayer.play());
		videoPlayer.on("ended", () => {
			console.log("⏹️ Video ended");
			if (videoPlayer.isFullscreen()) {
				videoPlayer.exitFullscreen();
			}
		});
		videoPlayer.on("pause", () => {
			console.log("Player pause ⏸️");
			if (!selectedLiveStream.hasEnded) {
				handleUpdateStream(streamId, localStorage.getItem(`stream-start-${streamId}`) as string);
			}
		});

		return videoPlayer;
	}, [handleUpdateStream, selectedLiveStream, streamId]);

	useEffect(() => {
		if (selectedLiveStream && !isFetchingStream && !player.current) {
			player.current = configurePlayer();
		}
	}, [configurePlayer, isFetchingStream, isLoading, selectedLiveStream]);

	useEffect(() => {
		return () => {
			if (player.current) {
				dispatch(clearSelectedStream());
				player.current.dispose();
				player.current = null;
			}
		};
	}, [dispatch]);

	const publishStream = (streamId: string, token: string) => {
		console.log("🌀 --------> Publishing Stream", adaptorRef.current, streamId);
		player.current?.play();
		adaptorRef.current && adaptorRef.current.publish(streamId, token);
	};

	const toggleMute = () => {
		setIsMute(m => !m);
		!isMute ? adaptorRef.current?.muteLocalMic() : adaptorRef.current?.unmuteLocalMic();
	};

	const toggleCamera = () => {
		setIsCameraEnabled(m => !m);
		isCameraEnabled
			? adaptorRef.current?.turnOffLocalCamera(streamId)
			: adaptorRef.current?.turnOnLocalCamera(streamId);
	};

	const leaveStream = streamId => {
		console.log("🎦 leaveStream ☑️");
		if (adaptorRef.current) {
			getStreamStats(streamId);
			adaptorRef.current.stop(streamId);
			player.current?.pause();
			setIsOnline(false);
		}
	};

	const getStreamStats = streamId => {
		console.log("📊 Getting streams stats", streamId);
		adaptorRef.current?.getStats(streamId);
	};
	const handleChatMessageReceive = (comment: LessonComment) => {
		dispatch(AddNewComment(comment));
	};

	const handleSendWebRtcData = async (message: string) => {
		dispatch(postLiveStreamComment({ streamId, comment: message }));
	};

	const handleDeleteComment = (commentId: string) => {
		dispatch(deleteLiveStreamComment({ id, commentId }));
	};

	const handleUpdatePage = (page: number) => {
		dispatch(fetchLiveStreamComments({ streamId: id, page }));
	};

	return (
		<Wrapper
			heading="Stream"
			breadcrumb={breadcrumb}
			actions={
				<Box>
					{isOnline ? (
						<Button
							variant={"filled"}
							style={{ background: theme.palette.colors.red[400] }}
							onClick={() => leaveStream(streamId)}
							disabled={isLoading}
							loading={isLoading}
						>
							Stop Stream
						</Button>
					) : (
						<Button
							variant={"filled"}
							color={"info"}
							onClick={() => history.push(routes.stream.getPath())}
							disabled={false}
							loading={false}
						>
							Back
						</Button>
					)}
				</Box>
			}
		>
			{selectedLiveStream?.hasEnded && (
				<Alert style={{ margin: "16px 0" }} severity="info">
					Live event is no longer available.
				</Alert>
			)}
			<Grid container spacing={1}>
				<Grid item xs={9}>
					<VideoContainer>
						<video id="video-player-stream" preload="auto" className={"video-js vjs-default-skin"} autoPlay />
						{!selectedLiveStream?.hasEnded && (
							<Box
								position="absolute"
								width={50}
								height={100}
								bottom="36px"
								right="16px"
								display="flex"
								flexDirection="column"
								justifyContent="space-between"
								gridGap={8}
							>
								<IconButton onClick={toggleCamera}>{isCameraEnabled ? <Videocam /> : <VideocamOff />}</IconButton>
								<IconButton onClick={toggleMute}>{isMute ? <MicOff /> : <Mic />}</IconButton>
							</Box>
						)}
					</VideoContainer>
				</Grid>
				<Grid item xs={3}>
					<Box display={"flex"} flexDirection={"column"} gridGap={5}>
						<Card>
							<TextField
								variant="outlined"
								fullWidth
								value={`${GLOBAL_CONSTANTS.CLIENT_APP_URL}/streams/${id}/${streamId}`}
								InputProps={{
									readOnly: true,
									endAdornment: (
										<Box ml={2}>
											<FileCopy
												style={{ cursor: "pointer" }}
												onClick={async () => {
													await navigator.clipboard.writeText(
														`${GLOBAL_CONSTANTS.CLIENT_APP_URL}/streams/${id}/${streamId}`
													);
													dispatch(
														emit({
															message: "Stream link copied to clipboard",
															color: "success"
														})
													);
												}}
											/>
										</Box>
									)
								}}
							/>
						</Card>
						<Card>
							<Box>
								<Typography>Stream Details</Typography>
								<Box mt={5} display={"flex"} flexDirection={"column"}>
									<Box display={"flex"} alignItems={"center"} gridGap={10}>
										<StyledEye /> {selectedLiveStreamViewersCount}
									</Box>
								</Box>
							</Box>
						</Card>
					</Box>
				</Grid>
				<ChatComponent
					streamComments={streamComments}
					isFetchingComments={isFetchingComments}
					hasEnded={selectedLiveStream?.hasEnded}
					updatePage={handleUpdatePage}
					handleSendWebRtcData={handleSendWebRtcData}
					user={user}
					handleDeleteComment={handleDeleteComment}
				/>
			</Grid>
		</Wrapper>
	);
};

export default StreamVideo;
